/// <amd-module name="TFS/ThemeManagement/TfsThemeService" />
import { IVssObservableService, Services, VssObservableService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { trace } from "VSS/Platform/Trace";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { ClientTheme, IVssThemeService } from "VSS/Platform/Theming";

/**
 * Event triggered when the theme has changed
 */
export interface IThemeChangedEvent {
    /**
     * The selected theme
     */
    selectedTheme: ClientTheme;
}

/**
 * Service for getting the list of available themes and managing the selected theme for the user.
 */
export interface ITfsThemeService extends IVssObservableService<IThemeChangedEvent, "selectedThemeChanged"> {
    /**
     * Gets the id of the currently selected theme
     */
    getCurrentThemeId: () => Promise<string>;

    /**
     * Returns a list of all available themes
     */
    getThemes: () => Promise<ClientTheme[]>;

    /**
     * Returns url which will provide more themes
     */
    getBrowseMoreUrl: () => Promise<string>;

    /**
     * Applies the specified theme as the current theme for the page. Does not
     * acutally persist the theme setting for the current user.
     *
     * @param theme Theme to apply
     * @param isCustomUserTheme If this is a custom user theme, save it off in local storage
     */
    applyTheme(theme: ClientTheme, isCustomUserTheme?: boolean): void;

    /**
     * Change the selected theme for the current user
     *
     * @param theme Newly selected theme
     */
    setTheme(theme: ClientTheme): Promise<void>;
}

class TfsThemeService extends VssObservableService<IThemeChangedEvent, "selectedThemeChanged"> implements ITfsThemeService {
    private currentThemeId: string;

    public getCurrentThemeId(): Promise<string> {
        if (this.currentThemeId) {
            return Promise.resolve(this.currentThemeId);
        } else {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            return contributionService.getDataAsync<{ currentThemeId: string }>("ms.vss-tfs-web.available-themes-data-provider").then(data => {
                this.currentThemeId = data!.currentThemeId;
                return this.currentThemeId;
            });
        }
    }

    public getThemes(): Promise<ClientTheme[]> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<{ themes: ClientTheme[] }>("ms.vss-tfs-web.available-themes-data-provider").then(data => {
            return data ? data.themes : [];
        });
    }

    public setTheme(theme: ClientTheme): Promise<void> {
        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        return settingsService.setEntries({ "WebPlatform/Theme": theme.id }, SettingsUserScope.GlobalMe).then(() => {
            this.applyTheme(theme, false);

            const telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            telemetryService.publishEvent("WebPlatform", "Theme", {
                selectedTheme: theme.id
            });
        });
    }

    public applyTheme(theme: ClientTheme, isCustomUserTheme?: boolean) {
        this.currentThemeId = theme.id;

        const themeService = this.pageContext.getService<IVssThemeService>("IVssThemeService");
        themeService.applyTheme(theme);
        themeService.saveCustomUserTheme(isCustomUserTheme ? theme : undefined);

        this._notify({ selectedTheme: theme }, "selectedThemeChanged");

        const themeChangedsEvent = new CustomEvent("themeChanged", { cancelable: true, detail: { ...theme } });
        document.body.dataset.theme = this.currentThemeId;
        trace(this.pageContext, { area: "theme", component: "TfsThemeService", exceptionType: "info", feature: "high-contrast", level: 3, message: `Applied theme: ${this.currentThemeId}`, method: "applyTheme", properties: {} })
        document.body.dispatchEvent(themeChangedsEvent);
    }

    public getBrowseMoreUrl(): Promise<string> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<{ marketplaceUrl: string }>("ms.vss-tfs-web.available-themes-data-provider").then(data => {
            return data ? data.marketplaceUrl : "";
        });
    }
}

Services.add("ITfsThemeService", { serviceFactory: TfsThemeService });
